import { motion } from "framer-motion";
import {
  Lock,
  KeyRound,
  FileCheck2,
  ShieldCheck,
  Coins,
} from "lucide-react";

// Change this if you're using @ alias
import { Eyebrow, Reveal } from "../components/SectionHeader";

const PILLARS = [
  {
    icon: Lock,
    title: "Encryption everywhere",
    body:
      "All data in transit and at rest is encrypted using industry-standard protocols.",
  },
  {
    icon: KeyRound,
    title: "Authenticated connections",
    body:
      "Every integration point uses strong authentication and access controls, including secure tunneling between systems.",
  },
  {
    icon: FileCheck2,
    title: "Full auditability",
    body:
      "Every message—whether an instruction, confirmation, or status update—is logged and traceable, supporting your compliance and reconciliation requirements.",
  },
  {
    icon: ShieldCheck,
    title: "Data integrity, not just transport",
    body:
      "We pass through the transaction and screening data our network partners and your compliance teams rely on without altering or stripping information needed for AML/CFT and sanctions checks.",
  },
  {
    icon: Coins,
    title: "Independent of the money chain",
    body:
      "Pay10x does not hold, pool, or have custody of client or transaction funds. Settlement is handled by licensed and regulated payment institutions operating each underlying network.",
  },
];

export default function Security() {
  return (
    <div>
      {/* Hero */}
      <section className="pt-40 pb-16 md:pb-24 bg-[color:var(--p10-ink)] text-white relative border-b border-white/10">
        <div className="grain"></div>

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <Eyebrow dark>Security &amp; Trust</Eyebrow>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="font-serif-display text-5xl sm:text-6xl lg:text-8xl leading-[0.98] mt-6 max-w-5xl"
          >
            How we protect
            <br />
            <em className="text-[color:var(--p10-warm)]">
              every transaction.
            </em>
          </motion.h1>

          <p className="mt-8 max-w-2xl text-lg text-white/70 leading-relaxed">
            Pay10x sits inside the secure message chain of global payment
            networks—a position we treat with the seriousness it deserves, even
            though we never take custody of funds.
          </p>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[color:var(--p10-line)] border border-[color:var(--p10-line)]">
            {PILLARS.map((pillar, index) => {
              const Icon = pillar.icon;

              return (
                <motion.div
                  key={pillar.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{
                    duration: 0.7,
                    delay: (index % 2) * 0.1,
                  }}
                  className={`bg-[color:var(--p10-ivory)] p-10 md:p-14 ${
                    index === 4 ? "md:col-span-2" : ""
                  }`}
                >
                  <div className="flex items-start gap-6">
                    <Icon className="w-8 h-8 text-[color:var(--p10-royal)] mt-1 shrink-0" />

                    <div>
                      <h3 className="font-serif-display text-3xl md:text-4xl leading-tight">
                        {pillar.title}
                      </h3>

                      <p className="mt-4 text-lg leading-relaxed text-[color:var(--p10-ink)]/75 max-w-2xl">
                        {pillar.body}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Structure */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="relative border border-[color:var(--p10-ink)]/10 bg-[color:var(--p10-warm)]/45 p-10 md:p-16">
            <span className="absolute top-6 left-6 eyebrow">
              A note on structure
            </span>

            <div className="pt-14 max-w-4xl">
              <Reveal>
                <p className="font-serif-display text-2xl md:text-3xl leading-[1.35] text-[color:var(--p10-ink)]">
                  Pay10x operates as a technology and connectivity provider. We
                  are not a licensed payment institution and do not provide
                  regulated payment services in our own right.
                </p>

                <p className="mt-8 text-lg leading-relaxed text-[color:var(--p10-ink)]/75">
                  We work under formal onboarding and API agreements with the
                  global payment networks we connect to, supporting our clients'
                  compliance obligations by maintaining data integrity and
                  transparency throughout the message chain.
                </p>

                <p className="mt-4 text-lg leading-relaxed text-[color:var(--p10-ink)]/75">
                  Businesses using Pay10x should ensure that their own licensing
                  and regulatory obligations are independently satisfied for the
                  jurisdictions in which they operate.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}